/**
 * UsersPage — Admin user management
 * Creates real Supabase auth accounts + app_user_roles via /api/admin-users
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Spinner } from '../components/ui';
import { supabase } from '../lib/supabase';
import type { UserRole } from '../hooks/useUserRole';

interface AppUser {
  id: string;
  user_email: string;
  role: UserRole;
  display_name: string | null;
  created_at: string;
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'medewerker', label: 'Medewerker' },
  { value: 'callcenter', label: 'Extern loket (callcenter)' },
];

const ROLE_BADGE_VARIANT: Record<UserRole, 'primary' | 'success' | 'info'> = {
  admin: 'primary',
  medewerker: 'success',
  callcenter: 'info',
};

// Icons
const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);
const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);
const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);
const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);
const XIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const KeyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  </svg>
);
const EnvelopeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

type ToastType = 'success' | 'error';

async function getAuthHeader(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

async function adminAction(body: Record<string, unknown>): Promise<{ success?: boolean; message?: string; error?: string; users?: AppUser[] }> {
  const headers = await getAuthHeader();
  const resp = await fetch('/api/admin-users', { method: 'POST', headers, body: JSON.stringify(body) });
  return resp.json();
}

export function UsersPage() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', display_name: '', role: 'callcenter' as UserRole });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('callcenter');
  const [editLoading, setEditLoading] = useState(false);

  // Delete confirm state
  const [deletingUser, setDeletingUser] = useState<AppUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Action loading per user (for reset/resend)
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: ToastType) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    const result = await adminAction({ action: 'list' });
    if (result.error) {
      showToast('Kon gebruikers niet laden: ' + result.error, 'error');
    } else {
      setUsers(result.users || []);
    }
    setIsLoading(false);
  }, [showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // --- Add user via API ---
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);

    const result = await adminAction({
      action: 'create',
      email: addForm.email,
      display_name: addForm.display_name,
      role: addForm.role,
    });

    if (result.error) {
      setAddError(result.error);
      setAddLoading(false);
      return;
    }

    setShowAddModal(false);
    setAddForm({ email: '', display_name: '', role: 'callcenter' });
    setAddLoading(false);
    showToast(result.message || 'Gebruiker aangemaakt!', 'success');
    fetchUsers();
  };

  // --- Edit role (still direct Supabase — only changes app_user_roles) ---
  const startEdit = (user: AppUser) => { setEditingId(user.id); setEditRole(user.role); };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (userId: string) => {
    setEditLoading(true);
    const result = await adminAction({ action: 'update_role', user_id: userId, role: editRole });
    if (result.error) {
      showToast('Fout bij bijwerken: ' + result.error, 'error');
    } else {
      setEditingId(null);
      fetchUsers();
    }
    setEditLoading(false);
  };

  // --- Delete user via API ---
  const confirmDelete = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    const result = await adminAction({ action: 'delete', email: deletingUser.user_email });
    if (result.error) {
      showToast(result.error, 'error');
    } else {
      showToast(result.message || 'Gebruiker verwijderd.', 'success');
      fetchUsers();
    }
    setDeletingUser(null);
    setDeleteLoading(false);
  };

  // --- Reset password ---
  const handleResetPassword = async (user: AppUser) => {
    setActionLoading(user.id);
    const result = await adminAction({ action: 'reset_password', email: user.user_email });
    if (result.error) {
      showToast(result.error, 'error');
    } else {
      showToast(result.message || 'Reset-link verstuurd.', 'success');
    }
    setActionLoading(null);
  };

  // --- Resend invite ---
  const handleResendInvite = async (user: AppUser) => {
    setActionLoading(user.id);
    const result = await adminAction({ action: 'resend_invite', email: user.user_email });
    if (result.error) {
      showToast(result.error, 'error');
    } else {
      showToast(result.message || 'Uitnodiging verstuurd.', 'success');
    }
    setActionLoading(null);
  };

  return (
    <>
      <div className="p-6 max-w-5xl mx-auto w-full">
        {/* Toast notification */}
        {toast && (
          <div className={`fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-heading font-semibold text-main">Gebruikers</h1>
          <Button leftIcon={<PlusIcon />} onClick={() => { setAddError(null); setShowAddModal(true); }}>
            Gebruiker toevoegen
          </Button>
        </div>

        {/* Users table */}
        <Card variant="elevated" padding="none">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-muted text-sm">Geen gebruikers gevonden.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left font-medium text-muted px-6 py-3">Naam</th>
                    <th className="text-left font-medium text-muted px-6 py-3">Email</th>
                    <th className="text-left font-medium text-muted px-6 py-3">Rol</th>
                    <th className="text-right font-medium text-muted px-6 py-3">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-3.5 text-main">
                        {user.display_name || <span className="text-muted italic">—</span>}
                      </td>
                      <td className="px-6 py-3.5 text-main">{user.user_email}</td>
                      <td className="px-6 py-3.5">
                        {editingId === user.id ? (
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value as UserRole)}
                            className="h-8 px-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <Badge variant={ROLE_BADGE_VARIANT[user.role]}>{user.role}</Badge>
                        )}
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {editingId === user.id ? (
                            <>
                              <button onClick={() => saveEdit(user.id)} disabled={editLoading}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50" title="Opslaan">
                                <CheckIcon />
                              </button>
                              <button onClick={cancelEdit} disabled={editLoading}
                                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50" title="Annuleren">
                                <XIcon />
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEdit(user)}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" title="Rol bewerken">
                                <PencilIcon />
                              </button>
                              <button onClick={() => handleResetPassword(user)} disabled={actionLoading === user.id}
                                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors disabled:opacity-50" title="Wachtwoord resetten">
                                <KeyIcon />
                              </button>
                              <button onClick={() => handleResendInvite(user)} disabled={actionLoading === user.id}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50" title="Uitnodiging opnieuw versturen">
                                <EnvelopeIcon />
                              </button>
                              <button onClick={() => setDeletingUser(user)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Verwijderen">
                                <TrashIcon />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-muted">
          Gebruikers krijgen automatisch een uitnodigingsmail. Met de 🔑 knop stuur je een wachtwoord-reset link. Met de ✉️ knop verstuur je de uitnodiging opnieuw.
        </p>
      </div>

      {/* --- Add User Modal --- */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <form onSubmit={handleAdd}>
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-heading font-semibold text-main mb-4">Gebruiker toevoegen</h2>
                {addError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{addError}</div>
                )}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-main">E-mail</label>
                    <input type="email" required value={addForm.email}
                      onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full h-10 px-3 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="naam@cincocleaning.com" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-main">Naam</label>
                    <input type="text" value={addForm.display_name}
                      onChange={(e) => setAddForm((f) => ({ ...f, display_name: e.target.value }))}
                      className="w-full h-10 px-3 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Volledige naam" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-main">Rol</label>
                    <select value={addForm.role}
                      onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value as UserRole }))}
                      className="w-full h-10 px-3 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <Button type="button" variant="secondary" onClick={() => setShowAddModal(false)} disabled={addLoading}>Annuleren</Button>
                <Button type="submit" isLoading={addLoading}>Toevoegen</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Delete Confirmation Modal --- */}
      {deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 pt-6 pb-4">
              <h2 className="text-lg font-heading font-semibold text-main mb-2">Gebruiker verwijderen</h2>
              <p className="text-sm text-muted">
                Weet je zeker dat je <strong>{deletingUser.display_name || deletingUser.user_email}</strong> wilt verwijderen?
                Dit verwijdert zowel het account als de rol. De gebruiker kan hierna niet meer inloggen.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <Button variant="secondary" onClick={() => setDeletingUser(null)} disabled={deleteLoading}>Annuleren</Button>
              <Button variant="danger" onClick={confirmDelete} isLoading={deleteLoading}>Verwijderen</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
